import { Controller } from "@hotwired/stimulus"
import { getColorClasses, createToken, positionFixedDropdown, toggleClearButton } from "../utils/dropdown_utils"

export default class extends Controller {
  static targets = ["input", "dropdown", "tokenContainer", "optionsContainer", "clearBtn", "search", "loadingMore", "loading", "error", "select", "option"]
  static values = {
    color: { type: String, default: "blue" },
    tokenClass: String,
    searchUrl: String,
    debounce: { type: Number, default: 300 }
  }

  connect() {
    // Server renders all initial UI — tokens, clearBtn, placeholder.
    // connect() only initialises JS state.
    this.debounceTimer = null
    this.currentQuery = ""
    this.offset = 0
    this.hasMore = true
    this.loading = false
    this.loadedIds = new Set()
    this.abortController = null
    this.repositionHandler = this.repositionIfOpen.bind(this)
  }

  disconnect() {
    if (this.abortController) this.abortController.abort()
    clearTimeout(this.debounceTimer)
    this.teardownFixedListeners()
  }

  renderToken(id, name) {
    const tokenClass = this.hasTokenClassValue ? this.tokenClassValue : null
    const token = createToken(id, name, getColorClasses(this.colorValue), "click->async-tokenized-select#removeToken", tokenClass)
    this.tokenContainerTarget.insertBefore(token, this.inputTarget)
  }

  showDropdown() {
    if (this._suppressNextOpen) {
      this._suppressNextOpen = false
      return
    }
    if (!this.dropdownTarget.classList.contains("hidden")) return

    this.dropdownTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
    positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
    this.setupFixedListeners()
    this.searchTarget.value = ""
    this.currentQuery = ""
    this.offset = 0
    this.hasMore = true
    this.loadedIds.clear()
    this.optionsContainerTarget.replaceChildren()
    this.hideError()
    this.showLoading()
    this.fetchOptions()
    requestAnimationFrame(() => this.searchTarget.focus())
  }

  // Opening is triggered by focus on the combobox (and this handler). Using toggle on the
  // container would run after focus in the same click: open then immediately close.
  handleContainerClick() {
    if (this.dropdownTarget.classList.contains("hidden")) {
      this.showDropdown()
    } else {
      this.searchTarget.focus()
    }
  }

  hideDropdown() {
    this.clearOptionFocus()
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.dropdownTarget.classList.add("hidden")
    this.teardownFixedListeners()
  }

  setupFixedListeners() {
    window.addEventListener("resize", this.repositionHandler)
    window.addEventListener("scroll", this.repositionHandler, true)
  }

  teardownFixedListeners() {
    if (!this.repositionHandler) return
    window.removeEventListener("resize", this.repositionHandler)
    window.removeEventListener("scroll", this.repositionHandler, true)
  }

  repositionIfOpen() {
    if (this.dropdownTarget.classList.contains("hidden")) return
    positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
  }

  handleKeydown(event) {
    const { key } = event

    if (key === "Escape") {
      event.preventDefault()
      this.hideDropdown()
      this._suppressNextOpen = true
      this.inputTarget.focus()
      return
    }

    if (key === "ArrowDown" || key === "ArrowUp") {
      event.preventDefault()
      this.moveOptionFocus(key === "ArrowDown" ? 1 : -1)
      return
    }

    if (key === "Enter") {
      event.preventDefault()
      const focused = this.element.querySelector(".ts-option-focused")
      if (focused) focused.click()
    }
  }

  moveOptionFocus(direction) {
    const options = Array.from(
      this.optionsContainerTarget.querySelectorAll("[data-async-tokenized-select-target='option']")
    )
    if (options.length === 0) return

    const current = this.element.querySelector(".ts-option-focused")
    const currentIndex = current ? options.indexOf(current) : -1

    this.clearOptionFocus()

    const nextIndex = direction > 0
      ? (currentIndex < options.length - 1 ? currentIndex + 1 : 0)
      : (currentIndex > 0 ? currentIndex - 1 : options.length - 1)

    options[nextIndex].classList.add("ts-option-focused")
    options[nextIndex].scrollIntoView({ block: "nearest" })
    if (options[nextIndex].id) {
      this.inputTarget.setAttribute("aria-activedescendant", options[nextIndex].id)
    }
  }

  clearOptionFocus() {
    const focused = this.element.querySelector(".ts-option-focused")
    if (focused) focused.classList.remove("ts-option-focused")
  }

  onSearchInput() {
    clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      const query = this.searchTarget.value.trim()
      if (query !== this.currentQuery) {
        if (this.abortController) {
          this.abortController.abort()
          this.loading = false
        }
        this.currentQuery = query
        this.offset = 0
        this.hasMore = true
        this.loadedIds.clear()
        this.hideError()
        this.optionsContainerTarget.replaceChildren()
        this.showLoading()
        this.fetchOptions()
      }
    }, this.debounceValue)
  }

  onScroll() {
    if (this.loading || !this.hasMore) return
    const container = this.optionsContainerTarget
    if (container.scrollHeight - container.scrollTop - container.clientHeight < 50) {
      this.fetchOptions()
    }
  }

  async fetchOptions() {
    if (this.loading || !this.hasMore) return

    this.loading = true
    const request = new AbortController()
    this.abortController = request
    if (this.offset > 0) this.showLoadingMore()

    const url = new URL(this.searchUrlValue, window.location.origin)
    if (this.currentQuery) url.searchParams.set("q", this.currentQuery)
    url.searchParams.set("offset", this.offset)

    try {
      const response = await fetch(url, { signal: request.signal })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      const wasFirstPage = this.offset === 0
      this.hasMore = data.has_more
      this.offset += data.owners.length

      this.hideLoading()
      this.appendOptions(data.owners)

      if (!this.dropdownTarget.classList.contains("hidden") && wasFirstPage) {
        positionFixedDropdown(this.dropdownTarget, this.tokenContainerTarget)
      }

      if (data.owners.length === 0 && this.offset === 0) this.showNoResults()
    } catch (error) {
      if (error.name === "AbortError") return
      this.hideLoading()
      this.showError()
    } finally {
      if (this.abortController === request) {
        this.loading = false
        this.hideLoadingMore()
      }
    }
  }

  selectedValues() {
    return new Set(
      Array.from(this.selectTarget.options)
        .filter((opt) => opt.selected)
        .map((opt) => String(opt.value))
    )
  }

  appendOptions(owners) {
    const selected = this.selectedValues()

    owners.forEach((owner) => {
      const id = String(owner.id)
      if (this.loadedIds.has(id) || selected.has(id)) return

      this.loadedIds.add(id)

      const option = document.createElement("div")
      option.id = `async-tokenized-select-option-${id}`
      option.className = "px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", "false")
      option.dataset.asyncTokenizedSelectTarget = "option"
      option.dataset.id = id
      option.dataset.name = owner.name
      option.dataset.action = "click->async-tokenized-select#selectOption"
      option.textContent = owner.name

      this.optionsContainerTarget.appendChild(option)
    })
  }

  selectOption(event) {
    event.stopPropagation()
    this.clearOptionFocus()
    const option = event.currentTarget
    const id = option.dataset.id
    const name = option.dataset.name

    this.addToken(id, name)
    option.remove()
    this.loadedIds.delete(id)
  }

  addToken(id, name) {
    let selectOption = this.selectTarget.querySelector(`option[value="${id}"]`)
    if (!selectOption) {
      selectOption = document.createElement("option")
      selectOption.value = id
      selectOption.textContent = name
      this.selectTarget.appendChild(selectOption)
    }
    selectOption.selected = true

    this.renderToken(id, name)
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    this.updateInputPlaceholder()
  }

  removeToken(event) {
    event.stopPropagation()
    const token = event.currentTarget.closest("[data-token-id]")
    const id = token.dataset.tokenId

    const selectOption = this.selectTarget.querySelector(`option[value="${id}"]`)
    if (selectOption) selectOption.selected = false

    token.remove()
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    this.updateInputPlaceholder()
  }

  clearAll(event) {
    event.stopPropagation()
    Array.from(this.selectTarget.options).forEach((opt) => { opt.selected = false })
    this.tokenContainerTarget.querySelectorAll("[data-token-id]").forEach((t) => t.remove())
    toggleClearButton(this.hasClearBtnTarget ? this.clearBtnTarget : null, this.tokenContainerTarget)
    this.updateInputPlaceholder()
    this.hideDropdown()
  }

  updateInputPlaceholder() {
    const hasTokens = !!this.tokenContainerTarget.querySelector("[data-token-id]")
    this.inputTarget.placeholder = hasTokens ? "Add more..." : "Select..."
  }

  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
      this.loadingTarget.classList.add("block")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
      this.loadingTarget.classList.remove("block")
    }
  }

  showNoResults() {
    const msg = document.createElement("div")
    msg.className = "px-3 py-2 text-sm text-gray-500 text-center"
    msg.textContent = "No results found"
    this.optionsContainerTarget.replaceChildren(msg)
  }

  showLoadingMore() {
    if (this.hasLoadingMoreTarget) this.loadingMoreTarget.classList.remove("hidden")
  }

  hideLoadingMore() {
    if (this.hasLoadingMoreTarget) this.loadingMoreTarget.classList.add("hidden")
  }

  showError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("hidden")
    } else {
      const msg = document.createElement("div")
      msg.className = "px-3 py-2 text-sm text-red-500 text-center"
      msg.textContent = "Failed to load. Please try again."
      this.optionsContainerTarget.replaceChildren(msg)
    }
  }

  hideError() {
    if (this.hasErrorTarget) this.errorTarget.classList.add("hidden")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) this.hideDropdown()
  }
}
